import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AccountNode } from '../../types/bookkeeping.js';

interface AccountTreeNodeProps {
  node: AccountNode;
  depth?: number;
}

function AccountTreeNode({ node, depth = 0 }: AccountTreeNodeProps) {
  const { t } = useTranslation('bookkeeping');
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children.length > 0;

  return (
    <div>
      <div
        className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50"
        style={{ paddingLeft: `${(depth + 1) * 12}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="w-4 text-slate-400 hover:text-slate-600"
          >
            {expanded ? '▾' : '▸'}
          </button>
        ) : (
          <span className="w-4" />
        )}
        <span className="font-mono text-xs text-slate-500">{node.code}</span>
        <span className={node.is_seeded ? 'text-slate-500' : 'font-medium text-slate-800'}>
          {node.name}
        </span>
        <span className="ml-1 text-xs text-slate-400">{node.currency}</span>
        {node.is_seeded && (
          <span className="ml-auto rounded-sm bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500">
            {t('accounts.seededBadge')}
          </span>
        )}
      </div>
      {expanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <AccountTreeNode key={child.id} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

interface AccountTreeProps {
  nodes: AccountNode[];
}

export function AccountTree({ nodes }: AccountTreeProps) {
  return (
    <div className="rounded border bg-white">
      {nodes.map((node) => (
        <AccountTreeNode key={node.id} node={node} />
      ))}
    </div>
  );
}
